import { useGlobalQuery, useGlobalMutation } from 'state/query-client/hooks';
import { getRoles, getUsers, IamData, RoleData, createBulkUsers, CreateBulkUsersPayload, createGroup, getGroupsAndUsers, getGroupById, removeUserFromGroup, deleteIamGroup, getGroupsByIds } from '../services/user-service';
import { useConfig } from '../../../hooks/use-config';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deactivateAccount } from '../../profile/services/accounts-service';
import { useToast } from '../../../hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { ErrorResponse, useCustomToast } from "features/auth/hooks/use-custom-toast/use-custom-toast";

export interface UserFilter {
  email?: string;
  name?: string;
  organization?: string;
  organizationIds?: string[];
  userIds?: string[];
}

export interface GetUsersPayload {
  page: number;
  pageSize: number;
  filter?: UserFilter;
  enabled: boolean;
}

export interface GetUsersResponse {
  data: IamData[];
  totalCount: number;
}

/**
 * Custom hook to fetch users from the API with pagination and optional filters.
 *
 * @param {GetUsersPayload} payload - The payload for the query containing pagination and filter options.
 * @returns {UseQueryResult<GetUsersResponse>} - The result of the query, including the fetched user data and loading state.
 *
 * @example
 * const { data, isLoading } = useGetUsersQuery({ page: 1, pageSize: 10 });
 */

export const useGetUsersQuery = (payload: GetUsersPayload) => {
  const { configurationSettings } = useConfig();

  return useGlobalQuery({
    queryKey: ['getUsers', payload.page, payload.pageSize, payload.filter],
    queryFn: () => getUsers(payload, configurationSettings),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    // placeholderData: (previousData) => previousData,
    enabled: payload.enabled,
  });
};

export interface RoleSort {
  property: string;
  isDescending: boolean;
}

export interface RoleFilter {
  search?: string;
}

export interface GetRolesPayload {
  page: number;
  pageSize: number;
  sort?: RoleSort;
  filter?: RoleFilter;
  projectKey?: string;
}

export interface GetRolesResponse {
  data: RoleData[];
  totalCount: number;
}

/**
 * Custom hook to fetch roles from the API with pagination, sorting, and filtering options.
 *
 * @param {GetRolesPayload} payload - The payload for the query containing pagination, sorting, and filter options.
 * @returns {UseQueryResult<GetRolesResponse>} - The result of the query, including the fetched roles data and loading state.
 *
 * @example
 * const { data, isLoading } = useGetRolesQuery({ page: 1, pageSize: 10 });
 */
export const useGetRolesQuery = (payload: GetRolesPayload) => {
  const { configurationSettings } = useConfig();

  return useGlobalQuery({
    queryKey: [
      'getRoles',
      payload.page,
      payload.pageSize,
      payload.filter,
      payload.sort,
      payload.projectKey,
    ],
    queryFn: () => getRoles(payload, configurationSettings),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Custom hook to deactivate a user account.
 *
 * @returns {UseMutationResult} - The mutation result for deactivating a user account.
 *
 * @example
 * const { mutateAsync: deactivateUser } = useDeactivateUser();
 * await deactivateUser(userId);
 */
export const useDeactivateUser = () => {
  const { configurationSettings } = useConfig();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (userId: string) => deactivateAccount(configurationSettings, userId),
    onSuccess: () => {
      // Invalidate and refetch users query to update the table
      queryClient.invalidateQueries({ queryKey: ['getUsers'] });

      // Show success toast
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('ACCOUNT_DEACTIVATED_SUCCESSFULLY'),
      });
    },
    onError: (error) => {
      // Show error toast
      toast({
        variant: 'destructive',
        title: t('SOMETHING_WENT_WRONG'),
        description: error?.message ?? t('ACCOUNT_DEACTIVATION_FAILED'),
      });
    },
  });
};

/**
 * Custom hook to create multiple users in bulk.
 *
 * @param {Object} [options] - Optional configuration for mutation.
 * @param {Function} [options.onSuccess] - Callback function to be called when the bulk user creation is successful.
 *
 * @returns {Object} The mutation result for creating bulk users.
 *
 * @example
 * const { mutate } = useCreateBulkUsers({
 *   onSuccess: () => {
 *     // handle success logic
 *   }
 * });
 */
export const useCreateBulkUsers = (options?: { onSuccess?: () => void }) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const { configurationSettings } = useConfig();
  const queryClient = useQueryClient();

  return useGlobalMutation({
    mutationKey: ['createBulkUsers'],
    mutationFn: (data: CreateBulkUsersPayload) => createBulkUsers(data, configurationSettings),
    onSuccess: () => {
      // Invalidate and refetch users query to update the table
      queryClient.invalidateQueries({ queryKey: ['getUsers'] });

      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('BULK_USERS_CREATE_SUCCESS') || 'Users have been successfully invited',
      });

      options?.onSuccess?.();
    },
    onError: (error: any) => {
      // Check if the error response contains specific email errors
      // Handle multiple possible error structures
      let emailErrors: Record<string, string> | null = null;

      // Try different paths to find the errors object
      if (error?.errors) {
        emailErrors = error.errors;
      } else if (error?.error?.errors) {
        emailErrors = error.error.errors;
      } else if (error?.data?.errors) {
        emailErrors = error.data.errors;
      }

      if (emailErrors && typeof emailErrors === 'object') {
        const emailErrorEntries = Object.entries(emailErrors);
        const existingEmails = emailErrorEntries.filter(([, message]) =>
          typeof message === 'string' && message.toLowerCase().includes('already exists')
        );

        if (existingEmails.length > 0) {
          const emailList = existingEmails.map(([email]) => email).join(', ');
          toast({
            variant: 'destructive',
            title: t('EMAIL_ALREADY_EXISTS') || 'Email Already Exists',
            description: `${t('FOLLOWING_EMAILS_ALREADY_EXIST') || 'The following emails already exist'}: ${emailList}`,
          });
          return;
        }
      }

      if (emailErrors && typeof emailErrors === 'object') {
        const emailErrorEntries = Object.entries(emailErrors);
        const errorMsg = emailErrorEntries.filter(([, message]) =>
          typeof message === 'string' && message.toLowerCase().includes('limit reached')
        );
        if (errorMsg) {
          toast({
            variant: 'destructive',
            title: t('SOMETHING_WENT_WRONG'),
            description: `${t('USER_LIMIT_REACHED') || 'User limit reached'}`,
          });
          return;
        }
      }

      // Default error handling
      toast({
        variant: 'destructive',
        title: t('SOMETHING_WENT_WRONG'),
        description: error?.error?.message ?? (t('BULK_USERS_CREATE_FAILED') || 'Failed to invite users'),
      });
    },
  });
};




export const useGroupCreate = () => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const { configurationSettings } = useConfig();


  interface CreateGroupData {
    ItemId: string,
    GroupName: string,
    GroupType: string,
    UserIds: string[],
    OwnerUserId: string,
    Source: string
  }

  return useGlobalMutation({
    mutationKey: ['createGroup'],

    mutationFn: (payload: CreateGroupData) =>
      createGroup({ ...payload }, configurationSettings),

    onSuccess: (e: any) => {
      if (e.errors && e.errors.message === 'Cannot Add Duplciate Group') {
        toast({
          variant: 'destructive',
          title: t('ERROR'),
          description: t('CANNOT_CREATE_DUPLICATE_GROUP'),
        });
      } else {
        toast({
          variant: 'success',
          title: t('SUCCESS'),
          description: t('GROUP_CREATED_SUCCESSFULLY'),
        });
      }
    },

    onError: ({ error }: any) => {
      toast({
        variant: 'destructive',
        title: t('ERROR'),
        description: error?.Message || t('GROUP_CREATION_FAILED'),
      });
    },
  });
};

export const useGroupsAndUsers = () => {
  const { showErrorToast } = useCustomToast();
  const { configurationSettings } = useConfig();

  interface CreateGroupData {
    page: number,
    pageSize: number,
    userPage: string,
    UserPageSize: string
  }

  return useGlobalMutation({
    mutationKey: ['getGroupsAndUsers'],
    mutationFn: (payload: CreateGroupData) => getGroupsAndUsers({ ...payload }, configurationSettings),

    onError: ({ error }: ErrorResponse) => {
      showErrorToast(error);
    },
  });
};


export const useGroupDetails = () => {
  const { showErrorToast } = useCustomToast();
  const { configurationSettings } = useConfig();

  interface getGroupById {
    groupId: string,
    page: number,
    pageSize: number
  }

  return useGlobalMutation({
    mutationKey: ['getGroupDetails'],
    mutationFn: (payload: getGroupById) => getGroupById({ ...payload }, configurationSettings),

    onError: ({ error }: ErrorResponse) => {
      showErrorToast(error);
    },
  });
};

export const useRemoveUserFromGroup = () => {
  const { showErrorToast } = useCustomToast();
  const { configurationSettings } = useConfig();
  const { toast } = useToast();
  const { t } = useTranslation();

  interface removeUserFromGroupPayloadType {
    userIds: string[],
    groupId: string,
  }

  return useGlobalMutation({
    mutationKey: ['removeUserFromGroup'],
    mutationFn: (payload: removeUserFromGroupPayloadType) => removeUserFromGroup({ ...payload }, configurationSettings),

    onSuccess: () => {
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('MEMBER_REMOVED_FROM_GROUP_SUCCESSFULLY'),
      });
    },
    onError: ({ error }: ErrorResponse) => {
      showErrorToast(error);
    },
  });
};


export const useDeleteIamGroup = () => {
  const { showErrorToast } = useCustomToast();
  const { configurationSettings } = useConfig();
  const { toast } = useToast();
  const { t } = useTranslation();

  interface deteleIamGroupPayloadType {
    userIds: string[],
    groupId: string,
  }

  return useGlobalMutation({
    mutationKey: ['deleteIamGroup'],
    mutationFn: (payload: deteleIamGroupPayloadType) => deleteIamGroup({ ...payload }, configurationSettings),

    onSuccess: () => {
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('GROUP_DELETED_SUCCESSFULLY'),
      });
    },
    onError: ({ error }: ErrorResponse) => {
      showErrorToast(error);
    },
  });
};

export const useRenameGroup = () => {
  const { showErrorToast } = useCustomToast();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { configurationSettings } = useConfig();


  interface CreateGroupData {
    ItemId: string,
    GroupName: string,
    GroupType: string,
    UserIds: string[],
    OwnerUserId: string,
    Source: string
  }

  return useGlobalMutation({
    mutationKey: ['renameGroup'],

    mutationFn: (payload: CreateGroupData) =>
      createGroup({ ...payload }, configurationSettings),

    onSuccess: () => {
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('GROUP_RENAMED_SUCCESSFULLY'),
      });
    },

    onError: ({ error }: ErrorResponse) => {
      showErrorToast(error);
    },
  });
};

export const useAddGroupMembers = () => {
  const { showErrorToast } = useCustomToast();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { configurationSettings } = useConfig();


  interface CreateGroupData {
    ItemId: string,
    GroupName: string,
    GroupType: string,
    UserIds: string[],
    OwnerUserId: string,
    Source: string
  }

  return useGlobalMutation({
    mutationKey: ['addMembersToGroup'],

    mutationFn: (payload: CreateGroupData) =>
      createGroup({ ...payload }, configurationSettings),

    onSuccess: () => {
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('MEMBERS_ADDED_TO_GROUP_SUCCESSFULLY')
      });
    },

    onError: ({ error }: ErrorResponse) => {
      showErrorToast(error);
    },
  });
};

export interface GetGroupsByIdsParams {
  groupIds: string[];
  enabled?: boolean;
}

export const useGetGroupsByIds = (params: GetGroupsByIdsParams) => {
  const { groupIds, enabled = true } = params;
  const { configurationSettings } = useConfig();

  return useGlobalQuery({
    queryKey: ['getGroupsByIds', groupIds],
    queryFn: () => getGroupsByIds({ GroupIds: groupIds }, configurationSettings),
    enabled: enabled && groupIds.length > 0,
  });
};